import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type KeyboardEvent, type MouseEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Ban, ChevronRight, RotateCcw, Search, ShieldCheck, ShieldOff, TriangleAlert, UserCog, Users as UsersIcon, X } from 'lucide-react'

import { hasPermission } from '@app/shared'

import { getCurrentUser, getUsers, patchUser } from '../api/admin'
import { UserDetail, type AccessChange } from './UserDetail'
import { PageHeader } from '../components/layout'
import {
  Avatar,
  Button,
  Callout,
  EmptyState,
  Field,
  IconButton,
  Input,
  Modal,
  Pager,
  Panel,
  SegmentedControl,
  Spacer,
  StatusPill,
  Table,
  TableSkeleton,
  Textarea,
  Toolbar,
  cx,
  describeError,
  money,
  statusLabel,
  tableClass,
  toNumber,
  useToast,
  useTotp,
  when,
  type TabOption,
} from '../components/ui/Ui'
import styles from './Users.module.css'

const ROLE_RU: Record<NonNullable<AccessChange['role']>, string> = { user: 'пользователь', admin: 'админ', support: 'поддержка', finance: 'финансы', content: 'контент' }

type StatusFilter = 'any' | 'active' | 'suspended' | 'banned'

const STATUS_FILTERS: TabOption<StatusFilter>[] = [
  { value: 'any', label: 'Все' },
  { value: 'active', label: 'Активные' },
  { value: 'suspended', label: 'Приостановлены' },
  { value: 'banned', label: 'Заблокированы' },
]

function changeTitle(change: AccessChange | null): string {
  if (change?.role) return `Сменить роль: ${ROLE_RU[change.row.role as keyof typeof ROLE_RU] ?? statusLabel(change.row.role)} → ${ROLE_RU[change.role]}`
  if (change?.status === 'active') return 'Восстановить доступ'
  if (change?.status === 'banned') return 'Заблокировать аккаунт'
  return 'Приостановить аккаунт'
}

function people(count: number): string {
  const tail = count % 100
  if (tail >= 11 && tail <= 14) return `${count} пользователей`
  if (count % 10 === 1) return `${count} пользователь`
  if (count % 10 >= 2 && count % 10 <= 4) return `${count} пользователя`
  return `${count} пользователей`
}

/**
 * People. The support desk finds a person and suspends or restores them; the
 * finance desk (`ledger`) opens the same card for the balances, the ledger
 * and a correction; the admin alone hands out desks. Blocking and the desk
 * live in the card, where the person is in front of the operator.
 */
export default function Users(): JSX.Element {
  const [params] = useSearchParams()
  const initialSearch = params.get('search') ?? ''
  const [search, setSearch] = useState(initialSearch)
  const [applied, setApplied] = useState(initialSearch)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [change, setChange] = useState<AccessChange | null>(null)
  const [reason, setReason] = useState('')
  const [opened, setOpened] = useState<string | null>(null)
  // The last card opened stays mounted while its drawer slides out; every opening starts a fresh card (empty correction form, new idempotency key).
  const [cardId, setCardId] = useState<string | null>(null)
  const [cardKey, setCardKey] = useState(0)
  const me = useQuery({ queryKey: ['me'], queryFn: getCurrentUser, staleTime: 60_000 })
  const role = me.data?.user.role ?? ''
  const canAdjust = hasPermission(role, 'ledger')
  const canManage = hasPermission(role, 'users')
  const canChangeRole = hasPermission(role, 'users.role')
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()

  const list = useQuery({
    queryKey: ['users', applied, status, page],
    queryFn: () => getUsers({ search: applied || undefined, status: status || undefined, page }),
  })

  const ask = (next: AccessChange): void => {
    setReason('')
    setChange(next)
  }

  const openCard = (id: string): void => {
    setCardId(id)
    setCardKey((key) => key + 1)
    setOpened(id)
  }

  const patch = useMutation({
    mutationFn: ({ row, status: nextStatus, role: nextRole, code }: AccessChange & { code: string }) =>
      patchUser(row.id, { ...(nextStatus ? { status: nextStatus } : {}), ...(nextRole ? { role: nextRole } : {}), ...(reason.trim() ? { reason: reason.trim() } : {}) }, code),
    onSuccess: (updated) => {
      toast(`${updated.email}: ${statusLabel(updated.status)}, роль — ${statusLabel(updated.role)}`)
      setChange(null)
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      void queryClient.invalidateQueries({ queryKey: ['user-detail', updated.id] })
    },
    onError: (error) => toast(describeError(error), true),
  })

  const apply = (): void => {
    setApplied(search.trim())
    setPage(1)
  }

  const reset = (): void => {
    setSearch('')
    setApplied('')
    setStatus('')
    setPage(1)
  }

  const rows = list.data?.items ?? []
  const filtered = applied !== '' || status !== ''
  const destructive = change !== null && change.status !== undefined && change.status !== 'active'

  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, id: string): void => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openCard(id)
    }
  }
  const stop = (event: MouseEvent): void => event.stopPropagation()

  return (
    <>
      <PageHeader title="Пользователи" description="Поиск по почте, доступ, KYC и баланс. Строка открывает карточку." />

      <Toolbar className={styles.toolbar}>
        <Input
          leading={<Search />}
          placeholder="Поиск по почте"
          aria-label="Поиск по почте"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') apply()
            if (event.key === 'Escape' && search !== '') {
              event.preventDefault()
              setSearch('')
            }
          }}
          trailing={
            search !== '' ? (
              <IconButton
                icon={<X />}
                label="Очистить поиск"
                size="sm"
                tooltip={false}
                className={styles.clear}
                onClick={() => {
                  setSearch('')
                  if (applied !== '') {
                    setApplied('')
                    setPage(1)
                  }
                }}
              />
            ) : undefined
          }
          className={styles.search}
        />
        <Button onClick={apply}>Найти</Button>
        <SegmentedControl
          ariaLabel="Статус"
          value={(status || 'any') as StatusFilter}
          options={STATUS_FILTERS}
          onChange={(next) => {
            setStatus(next === 'any' ? '' : next)
            setPage(1)
          }}
        />
        <Spacer />
        <span className={styles.total} aria-live="polite">
          {list.data ? (
            <>
              <span className="num">{list.data.total}</span> {people(list.data.total).replace(/^\d+ /, '')}
              {filtered ? ' найдено' : ' всего'}
            </>
          ) : null}
        </span>
      </Toolbar>

      {list.isError ? (
        <Panel>
          <EmptyState tone="error" icon={<TriangleAlert />} title="Список не загрузился" description={describeError(list.error)} action={<Button onClick={() => void list.refetch()}>Повторить</Button>} />
        </Panel>
      ) : list.isPending ? (
        <Panel>
          <TableSkeleton rows={8} headers={['Пользователь', 'Статус', 'KYC', 'USDT (спот)', 'Вход · 2FA', '']} />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<UsersIcon />}
            title={filtered ? 'Никого не найдено' : 'Пользователей пока нет'}
            description={filtered ? 'Измените поиск по почте или фильтр статуса.' : 'Здесь появятся все, кто зарегистрируется по приглашению.'}
            action={filtered ? <Button onClick={reset}>Сбросить фильтр</Button> : undefined}
          />
        </Panel>
      ) : (
        <Panel>
          <Table ariaLabel="Пользователи" key={`${applied}-${status}-${page}`}>
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Статус</th>
                <th>KYC</th>
                <th className={tableClass.right}>USDT (спот)</th>
                <th>Вход · 2FA</th>
                <th className={tableClass.right}>
                  <span className="sr-only">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody className={styles.rows}>
              {rows.map((row) => {
                const staff = row.role !== 'user'
                const self = row.id === me.data?.user.id
                const zero = (toNumber(row.spotUsdt) ?? 0) === 0
                return (
                  <tr
                    key={row.id}
                    className={cx(tableClass.clickable, cardId === row.id && opened !== null ? tableClass.selected : '')}
                    onClick={() => openCard(row.id)}
                    onKeyDown={(event) => onRowKey(event, row.id)}
                    tabIndex={0}
                  >
                    <td>
                      <div className={styles.person}>
                        <Avatar name={row.email} size={32} />
                        <div className={styles.personText}>
                          <span className={styles.email} title={row.email}>
                            {row.email}
                            {self ? <span className={styles.you}>вы</span> : null}
                          </span>
                          <span className={tableClass.sub}>
                            {staff ? (
                              <span className={styles.role}>
                                <UserCog aria-hidden="true" />
                                {ROLE_RU[row.role as keyof typeof ROLE_RU] ?? statusLabel(row.role)}
                              </span>
                            ) : null}
                            {staff ? ' · ' : ''}
                            рег. <span className="num">{new Date(row.createdAt).toLocaleDateString('ru-RU')}</span>
                            {row.emailVerified ? '' : <span className={styles.unverified}> · почта не подтверждена</span>}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <StatusPill status={row.status} />
                    </td>
                    <td>{row.kycStatus ? <StatusPill status={row.kycStatus} /> : <span className={tableClass.muted}>—</span>}</td>
                    <td className={cx(tableClass.num, zero ? tableClass.muted : '')}>{money(row.spotUsdt)}</td>
                    <td>
                      <span className={cx(row.lastLoginAt ? 'num' : tableClass.muted)}>{row.lastLoginAt ? when(row.lastLoginAt) : 'не входил'}</span>
                      <span className={cx(tableClass.sub, styles.twoFa, row.totpEnabled ? styles.twoFaOn : '')}>
                        {row.totpEnabled ? <ShieldCheck aria-hidden="true" /> : <ShieldOff aria-hidden="true" />}
                        {row.totpEnabled ? '2FA включена' : 'без 2FA'}
                      </span>
                    </td>
                    <td className={cx(tableClass.actions, styles.actions)}>
                      <span className={styles.rowActions} onClick={stop}>
                        {canManage && row.role !== 'master' && !self ? (
                          <span className={styles.reveal}>
                            {row.status !== 'active' ? (
                              <Button size="sm" icon={<RotateCcw />} onClick={() => ask({ row, status: 'active' })}>
                                Восстановить
                              </Button>
                            ) : (
                              <Button size="sm" variant="danger" onClick={() => ask({ row, status: 'suspended' })}>
                                Приостановить
                              </Button>
                            )}
                          </span>
                        ) : null}
                        <IconButton icon={<ChevronRight />} label="Открыть карточку" size="sm" tooltipSide="left" className={styles.open} onClick={() => openCard(row.id)} />
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
          {list.data ? <Pager page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} /> : null}
        </Panel>
      )}

      {cardId !== null ? (
        <UserDetail
          key={cardKey}
          userId={cardId}
          open={opened !== null}
          canAdjust={canAdjust}
          canManage={canManage && cardId !== me.data?.user.id}
          canChangeRole={canChangeRole && cardId !== me.data?.user.id}
          onAccessChange={ask}
          onClose={() => setOpened(null)}
        />
      ) : null}
      <Modal
        open={change !== null}
        onClose={() => setChange(null)}
        title={changeTitle(change)}
        subtitle={change?.row.email}
        icon={change?.role ? <UserCog /> : change?.status === 'active' ? <RotateCcw /> : <Ban />}
        tone={destructive ? 'down' : 'accent'}
        footer={
          <>
            <Button variant="quiet" onClick={() => setChange(null)}>
              Отмена
            </Button>
            <Button
              variant={change?.status === 'active' || change?.role ? 'primary' : 'danger'}
              loading={patch.isPending}
              disabled={reason.trim().length < 3}
              onClick={() => change && void withTotp((code) => patch.mutateAsync({ ...change, code }))}
            >
              Подтвердить
            </Button>
          </>
        }
      >
        {change?.role && change.role !== 'user' ? (
          <Callout tone="info">Стол «{ROLE_RU[change.role]}» получит доступ к разделам панели своей роли. Код подтверждения попросит следующий шаг.</Callout>
        ) : null}
        {change?.status === 'banned' ? <Callout tone="down">Пользователь потеряет доступ к аккаунту. Вернуть его можно кнопкой «Восстановить доступ» в карточке.</Callout> : null}
        <Field label="Причина" hint="Попадёт в журнал аудита. Не короче трёх символов.">
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </Modal>
    </>
  )
}
